// import { API } from 'constants/apiEndPoints'
import { EVENTS } from 'constants/events'
import { useBalanceObserver } from 'hooks/user/useBalanceObserver'
import { useUsernameObserver } from 'hooks/user/useUsername'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import { useCallback, useEffect, useRef } from 'react'
import { emitCustomEvent } from 'react-custom-events'
import { capitalizeFirst } from 'utils'

import { useCountryAllowanceObserver } from './useCountryAllowanceObserver'
import { User } from './user.d'
import { useSignInInfo } from './useSignInUpOut'
import { useUserUpdaters } from './useUser'
import { useSocketEmitter, useSocketListener } from 'hooks/sockets/useSocket'

type UserAuthError = {
  reason: string
  details: string
}

const getErrorToastObj = (errorObj: UserAuthError) => ({
  id: 'errorToast',
  autoClose: false,
  props: {
    message: {
      title: capitalizeFirst(errorObj?.details || errorObj?.reason || 'The order has been cancelled'),
      subtitle: 'Please try again',
    },
  },
})

export const useUserObserver = () => {
  const { signInInfo, isProxyWallet } = useSignInInfo()
  const { resetUser, updateUserWallet, updateUserProfile, updateUserPreferences } = useUserUpdaters()
  const { emit: gatewayEmit } = useSocketEmitter('gateway')
  const { yToast } = useYoloToast()
  const prevAccount = useRef('')

  //Profile Info
  const { username, email, avatar, emailValidated } = signInInfo
  //Wallet Info
  const { accessToken, account, chainId } = signInInfo

  const logOutUser = useCallback(() => {
    if (prevAccount.current || account) gatewayEmit('user.logout', { reason: 'logout' })
    prevAccount.current = ''
    resetUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetUser, gatewayEmit])

  const updateUser = useCallback(
    (userState: Subset<User>): void => {
      const { wallet, profile } = userState
      updateUserWallet(wallet)
      updateUserProfile(profile)
      updateUserPreferences({})
      gatewayEmit('user.auth', { token: wallet?.accessToken })
    },
    [updateUserWallet, updateUserProfile, updateUserPreferences, gatewayEmit],
  )

  // external but related observer
  useCountryAllowanceObserver([account, accessToken, isProxyWallet])
  useUsernameObserver()
  useBalanceObserver()

  // Track account changes
  useEffect(() => {
    if (!account) logOutUser()
    else {
      prevAccount.current = account
      updateUser({
        wallet: {
          account,
          accessToken,
          isProxy: isProxyWallet,
          chainId: Number(chainId),
        },
        profile: { username, avatar, email,emailValidated },
      })
    }
  }, [account, accessToken, isProxyWallet, updateUser, logOutUser, chainId, username, avatar, emailValidated])

  // Gateway user.auth.error listener
  useSocketListener('gateway','user.auth.error', (userAuthError: UserAuthError) => {
    emitCustomEvent(EVENTS.BALANCE_GATEWAY_UPDATE, userAuthError)
    yToast(getErrorToastObj(userAuthError))
  })
}
